import React from 'react';
import { getBgColorFromKey_TW } from 'flow/utils';
import {
    createFourDigitFromString,
    sliceUUIDFromUserName,
} from 'flow/utils/meeting-utils';
import { TypedMap } from 'flow/types/TypedMap';
import { MeetingMember } from 'flow/types/Meeting';

/**
 * A participant feed item as produced by useMemberManagement: the
 * conference-member record plus the stable grid position used for ordering.
 */
export type FeedMember = {
    member?: TypedMap<MeetingMember>;
    position: number;
};

/** A feed slot in the strip/grid: a remote participant or the local sender. */
export type FeedEntry =
    | { kind: 'remote'; member: FeedMember; key: string }
    | { kind: 'local'; key: string };

// The tile fills its absolutely-positioned wrapper; the wrapper carries the
// rect computed by the layout engine.
export const FILL_STYLE: React.CSSProperties = {
    width: '100%',
    height: '100%',
};

export const memberKey = (member: FeedMember, index: number): string =>
    member.member?.get('key') ?? `member-${index}`;

export const memberDisplayName = (member: FeedMember): string =>
    member.member?.get('displayName');

/**
 * Persistent state for {@link reorderSticky}. Hold one per ordering context in a
 * ref; never share between contexts. `order` is the current display order (by
 * key), `lastActive` the activity recency per key, `counter` a monotonic tick,
 * `signature` the last processed input fingerprint.
 */
export type StickyOrderState = {
    order: string[];
    lastActive: Record<string, number>;
    counter: number;
    signature: string;
};

export const createStickyOrderState = (): StickyOrderState => ({
    order: [],
    lastActive: {},
    counter: 0,
    signature: '',
});

// Reconcile the persisted state with the current item set: drop members who
// left — from the order and, crucially, from their recency too, so lastActive
// stays bounded over a long meeting and a returning member can't carry stale
// activity that resists eviction — then append new members in incoming order.
const reconcileOrder = <T>(
    state: StickyOrderState,
    keys: string[],
    byKey: Map<string, T>,
): void => {
    state.order = state.order.filter((key) => byKey.has(key));
    const groomedActivity: Record<string, number> = {};
    state.order.forEach((key) => {
        if (key in state.lastActive) {
            groomedActivity[key] = state.lastActive[key];
        }
    });
    state.lastActive = groomedActivity;
    keys.forEach((key) => {
        if (!state.order.includes(key)) {
            state.order.push(key);
        }
    });
};

// The window slot to give up when pulling in an off-window speaker: the
// longest-idle non-speaking occupant (ties prefer the highest index, nearest the
// overflow boundary, for minimal disruption). Returns -1 when the window is all
// speakers — then nobody is evicted.
const findEvictionIndex = <T>(
    state: StickyOrderState,
    windowSize: number,
    byKey: Map<string, T>,
    isSpeaking: (item: T) => boolean,
): number => {
    let evictIndex = -1;
    let evictActive = Infinity;
    for (let i = 0; i < windowSize && i < state.order.length; i++) {
        const key = state.order[i];
        if (isSpeaking(byKey.get(key))) {
            continue;
        }
        const active = state.lastActive[key] ?? 0;
        if (active <= evictActive) {
            evictActive = active;
            evictIndex = i;
        }
    }
    return evictIndex;
};

/**
 * Sticky, recency-aware ordering that keeps active speakers on screen without
 * reshuffling feeds already shown.
 *
 * `state` persists across renders (held in a ref by the caller). Items already
 * inside the window (first `windowSize`) are never moved — a feed that's already
 * visible doesn't jump when it starts speaking. A speaking item *outside* the
 * window is pulled in, taking the slot of the longest-idle non-speaking window
 * item (recency from `lastActive`; ties prefer the slot nearest the overflow
 * boundary, for minimal disruption). Because the order persists, a pulled-in
 * speaker stays after it stops talking until a newer off-window speaker
 * displaces the next longest-idle occupant. A speaker is never evicted for
 * another.
 *
 * Applied at two levels — the render cap (useMemberManagement) and the on-screen
 * count (MeetingMainArea). `keyOf` must return a stable per-item identity.
 *
 * `state` is mutated during render (not from an effect) on purpose: the ordered
 * result is needed for this frame's tiles, so an effect would cost a re-render
 * and a one-frame flash on every speaking change. That's safe because the
 * advance is idempotent — an input signature guards it, so it's a no-op on
 * unrelated re-renders, under StrictMode's double invocation, and for any
 * concurrent render started with the same inputs and discarded. (Mirrors the
 * existing positionsInGrid ref in useMemberManagement.)
 */
export const reorderSticky = <T>(
    state: StickyOrderState,
    items: T[],
    windowSize: number,
    keyOf: (item: T) => string,
    isSpeaking: (item: T) => boolean,
): T[] => {
    const byKey = new Map(items.map((item) => [keyOf(item), item]));
    const materialize = () => state.order.map((key) => byKey.get(key));

    const keys = Array.from(byKey.keys());
    const speakingKeys = keys.filter((key) => isSpeaking(byKey.get(key)));
    const signature = `${windowSize}|${keys.join(',')}|${speakingKeys.join(',')}`;

    // Recompute only when the inputs change — a no-op on unrelated re-renders,
    // and idempotent under React's double-invoked development render.
    if (signature === state.signature) {
        return materialize();
    }
    state.signature = signature;
    state.counter += 1;

    reconcileOrder(state, keys, byKey);
    speakingKeys.forEach((key) => {
        state.lastActive[key] = state.counter;
    });

    // Pull each off-window speaker into the window, giving up the longest-idle
    // occupant. Pulled-in speakers persist (sticky) via state.order.
    for (let hidden = windowSize; hidden < state.order.length; hidden++) {
        if (!isSpeaking(byKey.get(state.order[hidden]))) {
            continue;
        }
        const evictIndex = findEvictionIndex(
            state,
            windowSize,
            byKey,
            isSpeaking,
        );
        if (evictIndex === -1) {
            continue;
        }
        [state.order[evictIndex], state.order[hidden]] = [
            state.order[hidden],
            state.order[evictIndex],
        ];
    }
    return materialize();
};

/**
 * Stable per-participant background colour for the avatar of someone we hold no
 * contact for. Reuses the avatar flavour palette keyed off the name, so a
 * person keeps one colour wherever the app draws them.
 *
 * The join name is stripped first: the hash reads its first four characters,
 * which for a `<uuid>-<nick>` name are a uuid handed out afresh each join. So
 * the strip is what makes the colour the person's rather than the session's,
 * and what keeps callers holding a raw name and a stripped one in agreement.
 */
export const memberColorClass = (name: string): string =>
    getBgColorFromKey_TW(
        `contact-${createFourDigitFromString(sliceUUIDFromUserName(name))}`,
    );
